// Heads-up display: the DOM around the planet.

import { REGION_NAMES, coloByIata } from "../shared/colos";
import { CHAPTER_BARS, VOICE_LABEL, type Harmony } from "../shared/music";
import type { PlayerInfo, ServerHome, Stats } from "../shared/protocol";
import { escapeHtml } from "./labels";
import type { NetStatus } from "./net";

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

const fmt = new Intl.NumberFormat("en");

function latLonText(lat: number, lon: number): string {
  const ns = lat >= 0 ? "N" : "S";
  const ew = lon >= 0 ? "E" : "W";
  return `${Math.abs(lat).toFixed(0)}°${ns} ${Math.abs(lon).toFixed(0)}°${ew}`;
}

/** Best-effort place name for the subsolar point: the nearest data center's city. */
function sunPlace(h: Harmony): string {
  return latLonText(h.sunLat, h.sunLon);
}

export class Hud {
  private feed = $("feed");
  private toastEl = $("toast");
  private toastTimer: number | undefined;
  private lastKey = "";
  private narrationTimer: number | undefined;

  setStatus(s: NetStatus): void {
    const dot = $("live-dot");
    dot.classList.toggle("live", s === "live");
    dot.classList.toggle("offline", s === "offline");
    $("status-text").textContent =
      s === "live" ? "live" : s === "offline" ? "offline · ghosts only" : s === "reconnecting" ? "reconnecting…" : "connecting…";
  }

  setRoom(name: string): void {
    $("room-name").textContent = name;
  }

  setHarmony(h: Harmony, bar: number): boolean {
    $("key-name").textContent = h.rootName;
    $("mode-name").textContent = h.mode;
    $("sun-text").textContent = `sun overhead at ${sunPlace(h)}`;
    const into = ((bar % CHAPTER_BARS) + CHAPTER_BARS) % CHAPTER_BARS;
    $("chapter-bar").style.width = `${((into + 1) / CHAPTER_BARS) * 100}%`;
    const key = `${h.rootName} ${h.mode}`;
    const changed = this.lastKey !== "" && this.lastKey !== key;
    this.lastKey = key;
    return changed;
  }

  setStats(s: Stats, humans: number): void {
    $("online-count").textContent = fmt.format(humans);
    $("online-label").textContent = humans === 1 ? "musician on Earth" : "musicians on Earth";
    $("stat-notes").textContent = fmt.format(s.notesEver);
    $("stat-countries").textContent = fmt.format(s.countries);
    $("stat-colos").textContent = fmt.format(s.colos);
  }

  setYou(p: PlayerInfo, home: ServerHome | null): void {
    $("you-dot").style.background = `hsl(${p.hue} 90% 62%)`;
    $("you-dot").style.boxShadow = `0 0 12px hsl(${p.hue} 90% 62%)`;
    $("you-name").textContent = p.name;
    const colo = coloByIata(p.colo);
    const where = [p.city, p.country].filter(Boolean).join(", ");
    $("you-meta").textContent = `${where} · ${VOICE_LABEL[p.voice]}${colo ? ` · ${REGION_NAMES[colo.region]}` : ""}`;
    $("you-route").innerHTML = home
      ? `you → <b>${p.colo}</b> → <b>${home.colo}</b> ⌂ ${escapeHtml(home.city)}`
      : `you → <b>${p.colo}</b>`;
  }

  setLatency(rttMs: number, lightMs: number): void {
    $("rtt").textContent = Number.isFinite(rttMs) ? `${Math.round(rttMs)} ms` : "—";
    $("rtt-light").textContent = `${lightMs.toFixed(1)} ms`;
    const pct = Number.isFinite(rttMs) && rttMs > 0 ? Math.min(1, lightMs / rttMs) : 0;
    $("latency-fill").style.width = `${pct * 100}%`;
    $("latency").title = Number.isFinite(rttMs)
      ? `Your notes make the round trip at ${(pct * 100).toFixed(0)}% of the speed of light in fiber.`
      : "Measuring…";
  }

  feedItem(html: string, hue?: number): void {
    const el = document.createElement("div");
    el.className = "feed-item";
    el.innerHTML = (hue !== undefined ? `<span class="swatch" style="background:hsl(${hue} 90% 62%)"></span>` : "") + html;
    this.feed.prepend(el);
    while (this.feed.children.length > 5) this.feed.lastElementChild?.remove();
    window.setTimeout(() => el.classList.add("fade"), 6500);
    window.setTimeout(() => el.remove(), 8000);
  }

  narrate(text: string, by: "ai" | "poet"): void {
    const el = $("narration");
    el.classList.remove("show");
    window.setTimeout(() => {
      el.innerHTML = `<span class="narration-text">${escapeHtml(text)}</span><span class="narration-by">${by === "ai" ? "narrated by Workers AI" : "the planet, to itself"}</span>`;
      el.classList.add("show");
    }, 700);
    window.clearTimeout(this.narrationTimer);
    this.narrationTimer = window.setTimeout(() => el.classList.remove("show"), 26_000);
  }

  toast(html: string, ms = 3800): void {
    this.toastEl.innerHTML = html;
    this.toastEl.classList.add("show");
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => this.toastEl.classList.remove("show"), ms);
  }
}
